/**
 * The part of a content URL that belongs to the platform rather than to the
 * customer, and which the setup fields therefore leave out — the same job
 * serverSubFolder ("/custom/uedge.com") does on a hand-configured install.
 *
 * A cloud deployment has no serverSubFolder. Its root is
 * /content/<storage key>/apps/<frontend>: the tenant's space, then the folder
 * this frontend was given. Neither is chosen here and neither can be edited,
 * so showing them only makes the field harder to read. What is the customer's
 * starts at properties/.
 *
 * Shared by the Folder Structure panel and the AI Setup panel: both show the
 * same three folders, and a path that means one thing in one and another in the
 * other is how an import ends up reading someone else's files.
 */
export interface InstancePathConfig {
  serverSubFolder?: string;
  /** This deployment's conversion server — the only host that serves its content. */
  ServerBackEndUrl?: string;
  globalApplicationFilesLocation?: string;
  propertyJSONFilesLocation?: string;
  propertySVGFilesLocation?: string;
}

/**
 * Is this URL served by the conversion server this deployment actually talks
 * to?
 *
 * The question matters because a build ships with content URLs baked in — the
 * repo's own demo, on http://localhost:3000 — and a frontend whose config was
 * never rewritten still carries them. Reading a root out of one of those gave
 * "/content/custom" (from ".../content/custom/BPRetail/...") and the panel then
 * displayed "/BPRetail/zthcvtest.blob.core.windows.net/..." as if it were the
 * customer's own folder. It is not this instance's URL at all, and a page
 * served over https cannot even fetch it.
 */
export function servedByInstance(url: string, serverBase?: string): boolean {
  const text = String(url || "");
  if (!text) return false;
  // A path with no host is served by whoever serves the page.
  if (text.startsWith("/")) return true;
  let origin: string;
  try {
    origin = new URL(text).origin;
  } catch {
    return false;
  }
  if (!serverBase) return false;
  try {
    return origin === new URL(serverBase).origin;
  } catch {
    return false;
  }
}

export function instanceRootFrom(config: InstancePathConfig): string {
  const explicit = (config.serverSubFolder || "").replace(/\/$/, "");
  if (explicit) return explicit;
  for (const url of [
    config.globalApplicationFilesLocation,
    config.propertyJSONFilesLocation,
    config.propertySVGFilesLocation,
  ]) {
    // Only a URL this deployment actually serves can say where its root is.
    if (!servedByInstance(String(url || ""), config.ServerBackEndUrl)) continue;
    // The frontend's own folder counts as part of the root when it is there;
    // a deployment that predates it stops at the storage key.
    const found = /\/content\/[^/]+(?:\/apps\/[^/]+)?/.exec(String(url || ""));
    if (found) return found[0];
  }
  return "";
}

/**
 * Drop the instance root from a path, whatever shape it arrives in.
 * FolderSelectModal already strips "/content/" from what it returns, so the
 * same folder reaches us as "/content/demo/apps/…" from the config and as
 * "demo/apps/…" from the picker; both must end up as "/apps/…".
 */
export function stripInstance(path: string, instanceRoot: string): string {
  if (!instanceRoot) return path;
  const idx = path.indexOf(instanceRoot);
  if (idx !== -1) return path.substring(idx + instanceRoot.length);
  // The picker's form: the leading "/content" is gone, the key is not.
  const tail = instanceRoot.replace(/^\/content\//, "");
  const withoutSlash = path.replace(/^\//, "");
  if (tail && withoutSlash.startsWith(`${tail}/`)) {
    return withoutSlash.substring(tail.length);
  }
  return path;
}

/** A configured content URL, shown as the folder the customer owns. */
export function clipUrl(fullUrl: string, instanceRoot: string): string {
  let path = "";
  try {
    path = new URL(fullUrl).pathname;
  } catch {
    path = fullUrl || "";
  }
  path = stripInstance(path, instanceRoot);
  return path.startsWith("/") ? path : `/${path}`;
}

/**
 * The reverse: a folder the customer picked, back to a full content URL.
 *
 * `serverBase` is what keeps a corrected path from being pinned back onto a
 * host this deployment cannot reach. Picking the right folder used to rebuild
 * the URL on whatever origin the old value carried — http://localhost:3000 on a
 * frontend whose config was never rewritten — and the viewer then answered
 * "Failed to fetch" on a setting that looked perfectly correct.
 */
export function constructUrl(
  path: string,
  originalUrl: string,
  instanceRoot: string,
  serverBase?: string
): string {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const usable = servedByInstance(originalUrl, serverBase);

  if (usable && instanceRoot && originalUrl.includes(instanceRoot)) {
    const idx = originalUrl.indexOf(instanceRoot);
    const prefix = originalUrl.substring(0, idx + instanceRoot.length);
    return `${prefix}${cleanPath}`;
  }

  // Anything else is rebuilt on the server this deployment talks to. With no
  // instance root to prepend — a frontend whose config was never rewritten —
  // the picker hands back the whole path below the content root (storage key
  // and app folder included), so the only missing piece is "/content".
  const base = usable ? originalUrl : serverBase || originalUrl;
  const tail = instanceRoot
    ? `${instanceRoot}${cleanPath}`
    : cleanPath.startsWith("/content/")
      ? cleanPath
      : `/content${cleanPath}`;
  try {
    return `${new URL(base).origin}${tail}`;
  } catch {
    return tail; // Fallback
  }
}

/** What the analytics API is given: no leading or trailing slash. */
export function apiFolderPath(value: string): string {
  return (value || "").replace(/^\/+/, "").replace(/\/+$/, "");
}
